import * as React from 'react';
import { observer } from 'mobx-react';
import { Subscriber } from 'react-broadcast';

import { ComponentStore } from './ComponentStore';
import { ConfigProvider } from '../utils/ConfigProvider';
import { renderTemplate, evaluate } from '../utils/StringUtils';

export interface RendererComponentProps<T> {
  config: T;
}

interface RendererConfig {
  // If string, configuration is loaded from ConfigProvider. otherwise Passed through
  content: { [key: string]: any } | string[] | string | undefined;

  context?: { [key: string]: any };
}

interface RendererState {
  loaded: number;
}

@observer
export class RendererComponent extends React.Component<
  RendererConfig,
  RendererState
> {
  state = { loaded: 0 };

  getConfig(content: any): { [key: string]: any } | any[] | string | undefined {
    if (typeof content === 'string') {
      let res: any | null = ConfigProvider.findConfig(content);

      if (res) {
        return res;
      }

      try {
        res = renderTemplate(content, this.props.context);
        if (res.length === 0) {
          return res;
        }
      } catch (err) {
        console.log('rendering error', content, err);
        res = err.message;
      }

      return ConfigProvider.findConfig(res) || res || content;
    }
    return content;
  }

  getContent = (
    content: { [key: string]: any } | string,
    index: number = 0
  ) => {
    const config = this.getConfig(content);
    if (!config) {
      return null;
    }

    let configArray: any[];
    if (!Array.isArray(config)) {
      configArray = [config];
    } else {
      configArray = config;
    }

    const comps = configArray.map((conf: any, i: number) => {
      if (typeof conf === 'string') {
        return conf;
      }

      // console.log('render', conf);
      const Component = ComponentStore.shared().getComponent(
        conf.component
      ) as React.ComponentClass<RendererComponentProps<any>>;

      if (!Component) {
        ComponentStore.shared()
          .loadComponent(conf.component)
          .then(() => {
            this.setState({ loaded: this.state.loaded + 1 });
          });
        return null;
      }

      const visible = (conf as { [key: string]: any }).visible;
      if (
        typeof visible !== 'undefined' &&
        !evaluate(visible, this.props.context)
      ) {
        return null;
      }

      // console.log('renderer', this.props.context);
      return (
        <Component
          key={`${index}_${i}`}
          config={conf}
          {...this.props.context}
        />
      );
    });

    return comps;
  };

  getContentComponent = (): any => {
    if (!this.props.content) {
      return null;
    }

    if (Array.isArray(this.props.content)) {
      return this.props.content.map(this.getContent);
    } else {
      return this.getContent(this.props.content);
    }
  };

  render() {
    let comp = this.getContentComponent();
    return comp;
  }
}

export class Renderer extends React.Component<RendererConfig> {
  render() {
    if (this.props.context) {
      return (
        <RendererComponent
          content={this.props.content}
          context={this.props.context}
        />
      );
    }

    return (
      <Subscriber channel="context">
        {ctx => (
          <RendererComponent content={this.props.content} context={ctx} />
        )}
      </Subscriber>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/Renderer.tsx